import { c as _c } from "react/compiler-runtime";
import { MODAL } from "@website/accommodation-ui/constant";
import { MaterialIcon } from "@website/icons/material";
import { Button } from "@website/ui/button";
import { CarouselV2 } from "@website/ui/carousel";
import { LazyImage, type LazyImagePropertiesType } from "@website/ui/lazy-image";
import { Modal } from "@website/ui/modal";
import { Skeleton } from "@website/ui/skeleton";
import { starsHotelImage } from "@website/ui/stars-hotel-image";
import { getImage } from "@website/utils/get-image";
import { numberConvert } from "@website/utils/number-convert";
import { useCompatRouter } from "@website/widget-v2/helpers/compat-router.hook";
import { useReserveGalleryDetailContainer } from "@website/widget/reserve-gallery";
import { ReserveGalleryDetail, ReserveGallerySingle } from "@website/widget/reserve-gallery-detail";
import omit from "lodash-es/omit";
import { FC, MouseEvent, ReactEventHandler, useEffect, useRef, useState } from "react";
import { sanitizeAccommodationCardHref, sanitizeAccommodationCardQuery } from "../accommodation-card-link/accommodation-card-link.utils";
import { AccommodationCardMediaBadges } from "./accommodation-card-media-badges";
import { AccommodationCardMediaPropertiesType } from "./accommodation-card-media.types";
import styles from "./style.module.scss";
const GALLERY_MODAL_LIST = new Set(["gallery", "gallery-details"]);
const SELECTED_HOTEL_ID = "selectedHotelId";
const GALLERY_QUERY_KEYS = [MODAL, SELECTED_HOTEL_ID, "item", "index"];
const getQueryValue = (value?: string | Array<string>) => Array.isArray(value) ? value[0] : value;
const AccommodationCardImage: FC<LazyImagePropertiesType> = t0 => {
  const $ = _c(24);
  let fallback;
  let onLoad;
  let properties;
  let src;
  let t1;
  if ($[0] !== t0) {
    ({
      className: t1,
      fallback,
      onLoad,
      src,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = fallback;
    $[2] = onLoad;
    $[3] = properties;
    $[4] = src;
    $[5] = t1;
  } else {
    fallback = $[1];
    onLoad = $[2];
    properties = $[3];
    src = $[4];
    t1 = $[5];
  }
  const className = t1 === undefined ? "" : t1;
  const imageSourceKey = String(src || fallback || "");
  const containerReference = useRef(null);
  const [loadedSourceKey, setLoadedSourceKey] = useState();
  const isLoaded = loadedSourceKey === imageSourceKey;
  let t2;
  let t3;
  if ($[6] !== imageSourceKey) {
    t2 = () => {
      const image = containerReference.current?.querySelector("img");
      if (image?.complete) {
        setLoadedSourceKey(imageSourceKey);
      }
    };
    t3 = [imageSourceKey];
    $[6] = imageSourceKey;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== imageSourceKey || $[10] !== onLoad) {
    t4 = event => {
      setLoadedSourceKey(imageSourceKey);
      onLoad?.(event);
    };
    $[9] = imageSourceKey;
    $[10] = onLoad;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleLoad = t4;
  const t5 = `pointer-events-none absolute inset-0 transition-opacity ${isLoaded ? "opacity-0" : "opacity-100"}`;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Skeleton className="!rounded-[14px]" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5) {
    t7 = <div aria-hidden="true" className={t5}>{t6}</div>;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = `${className} ${isLoaded ? "opacity-100" : "opacity-0"}`;
  let t9;
  if ($[15] !== fallback || $[16] !== handleLoad || $[17] !== properties || $[18] !== src || $[19] !== t8) {
    t9 = <LazyImage {...properties} className={t8} fallback={fallback} onLoad={handleLoad} src={src} />;
    $[15] = fallback;
    $[16] = handleLoad;
    $[17] = properties;
    $[18] = src;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t7 || $[22] !== t9) {
    t10 = <div className="relative size-full" ref={containerReference}>{t7}{t9}</div>;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
export const AccommodationCardMedia: FC<AccommodationCardMediaPropertiesType> = properties => {
  const $ = _c(62);
  const {
    badges,
    title,
    gallery,
    isPriorityImage,
    thumbnail,
    detailLink,
    id,
    setClickLoading,
    clickLoading,
    onDetailClick
  } = properties;
  const router = useCompatRouter();
  let t0;
  if ($[0] !== gallery?.length || $[1] !== id || $[2] !== router.query) {
    const activeModal = getQueryValue(router?.query?.[MODAL]);
    const t1 = router?.query?.[SELECTED_HOTEL_ID];
    let t2;
    if ($[4] !== t1) {
      t2 = getQueryValue(t1);
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    const selectedHotelId = Number(t2);
    t0 = selectedHotelId === Number(id) && GALLERY_MODAL_LIST.has(activeModal || "") && (gallery?.length ?? 0) > 0;
    $[0] = gallery?.length;
    $[1] = id;
    $[2] = router.query;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const isGalleryModalOpen = t0;
  const t1 = gallery?.length ?? 0;
  let t2;
  if ($[6] !== t1) {
    t2 = {
      templateNumber: 4,
      galleryLength: t1
    };
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    containerProps
  } = useReserveGalleryDetailContainer(t2);
  let t3;
  if ($[8] !== id || $[9] !== router) {
    t3 = event => {
      event?.preventDefault();
      event?.stopPropagation();
      return router[router.pathname.includes("map") ? "replace" : "push"](router?.pathname, {
        ...router.query,
        selectedHotelId: id,
        [MODAL]: "gallery"
      }, {
        shallow: true,
        scroll: false
      });
    };
    $[8] = id;
    $[9] = router;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const showGallery = t3;
  let t4;
  if ($[11] !== router) {
    t4 = () => router.replace(router.pathname, omit(router?.query, GALLERY_QUERY_KEYS), {
      shallow: true,
      scroll: false
    });
    $[11] = router;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const closeGallery = t4;
  let t5;
  if ($[13] !== detailLink || $[14] !== onDetailClick || $[15] !== router || $[16] !== setClickLoading) {
    t5 = event_0 => {
      event_0.preventDefault();
      event_0.stopPropagation();
      const hasPathDetailLink = detailLink?.pathname && typeof detailLink.query !== "string";
      if (!detailLink?.href && !hasPathDetailLink) {
        return;
      }
      onDetailClick?.(event_0);
      setClickLoading?.(true);
      if (detailLink?.href) {
        const href = sanitizeAccommodationCardHref(detailLink.href);
        if (detailLink.target === "_blank") {
          window.open(href, "_blank", "noopener,noreferrer");
          setClickLoading?.(false);
          return;
        }
        return Promise.resolve(router.push(href, {}, {
          scroll: true
        })).finally(() => setClickLoading?.(false));
      }
      if (!detailLink.pathname || typeof detailLink.query === "string") {
        return;
      }
      return Promise.resolve(router.push(detailLink.pathname, sanitizeAccommodationCardQuery(omit(detailLink.query ?? {}, GALLERY_QUERY_KEYS)), {
        scroll: true
      })).finally(() => setClickLoading?.(false));
    };
    $[13] = detailLink;
    $[14] = onDetailClick;
    $[15] = router;
    $[16] = setClickLoading;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const showDetail = t5;
  let t6;
  if ($[18] !== badges || $[19] !== id) {
    t6 = <AccommodationCardMediaBadges badges={badges} id={id} />;
    $[18] = badges;
    $[19] = id;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== gallery || $[22] !== isPriorityImage || $[23] !== showGallery || $[24] !== thumbnail?.alt || $[25] !== thumbnail?.url || $[26] !== title) {
    t7 = gallery && (gallery?.length ?? 0) > 1 ? <CarouselV2 disableArrowKeys={true} hasDots={gallery && gallery?.length > 1} dotsNewDesign={true} hasNavigation={gallery && gallery?.length > 1 ? "fixed" as unknown as boolean : false} keyCarousel="villa-card-thumbnail" leftBtnClassName="rounded-lg left-2" list={[...(gallery?.length >= 5 ? gallery?.slice(0, 5) || [] : gallery || []).map((img, imageIndex) => <AccommodationCardImage alt={img?.title || "\u062A\u0635\u0627\u0648\u06CC\u0631 \u0647\u062A\u0644"} className="h-[220px] min-h-full w-full rounded-[14px] object-cover" fallback={starsHotelImage(1).src} key={img?.media} loading={isPriorityImage && imageIndex === 0 ? "eager" : "lazy"} src={getImage({
      width: 288,
      height: 288,
      src: img?.media
    })} title={title} />), <div className="relative size-full" key="load_gallery"><AccommodationCardImage className="h-[220px] min-h-full w-full rounded-[14px] object-cover blur-sm" fallback={starsHotelImage(1).src} loading={isPriorityImage ? "eager" : "lazy"} src={getImage({
        width: 288,
        height: 288,
        src: gallery?.[gallery?.length - 1]?.media
      })} title={title} alt={gallery?.[gallery?.length - 1]?.title || "\u062A\u0635\u0627\u0648\u06CC\u0631 \u0647\u062A\u0644"} key={gallery?.[gallery?.length - 1]?.media} /><Button StartIcon={<MaterialIcon className="bg-dim-background text-on-surface-high-emphasis" name="collections" variant="fill" />} className="text-caption !bg-dim-background !text-on-surface-high-emphasis !absolute inset-x-0 inset-y-1/2 z-10 m-auto w-[155px] !rounded-2xl" onClick={showGallery} size="large" text={"\u0639\u06A9\u0633\u200C\u0647\u0627\u06CC \u0628\u06CC\u0634\u062A\u0631"} /></div>]} mode="snap" options={{
      slides: {
        spacing: 16
      }
    }} rightBtnClassName="rounded-lg right-2" sectionClassName="rounded-[14px]" /> : <AccommodationCardImage className="h-[220px] min-h-full w-full rounded-[14px] object-cover" loading={isPriorityImage ? "eager" : "lazy"} src={getImage({
      width: 288,
      height: 288,
      src: thumbnail?.url
    })} title={title} key={gallery?.[gallery?.length - 1]?.media} alt={thumbnail?.alt || "\u062A\u0635\u0627\u0648\u06CC\u0631 \u0648\u06CC\u0644\u0627"} fallback={starsHotelImage(1).src} />;
    $[21] = gallery;
    $[22] = isPriorityImage;
    $[23] = showGallery;
    $[24] = thumbnail?.alt;
    $[25] = thumbnail?.url;
    $[26] = title;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== t6 || $[29] !== t7) {
    t8 = <div className={`${styles.card} accommodation-card-image relative flex h-[220px] shrink-0`}>{t6}{t7}</div>;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const t9 = router.locale || "fa";
  let t10;
  if ($[31] !== t9 || $[32] !== title) {
    t10 = numberConvert(title, {
      locale: t9
    });
    $[31] = t9;
    $[32] = title;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== containerProps || $[35] !== gallery) {
    t11 = containerProps?.map(t12 => {
      const {
        index,
        rowCount
      } = t12;
      return <ReserveGalleryDetail gallery={gallery ?? []} index={index} key={`AccommodationCardReserveGalleryDetailContainer-${index}`} rowCount={rowCount} />;
    });
    $[34] = containerProps;
    $[35] = gallery;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t11) {
    t12 = <div className="grid h-full grid-cols-2 gap-2 overflow-y-auto p-2 lg:px-[calc((100%-750px)/2)] lg:py-6">{t11}</div>;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== gallery) {
    t13 = gallery ?? [];
    $[39] = gallery;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t13) {
    t14 = <ReserveGallerySingle gallery={t13} />;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== closeGallery) {
    t15 = <Button color="neutral" onClick={closeGallery} text={"\u0628\u0633\u062A\u0646"} variant="text" />;
    $[43] = closeGallery;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== clickLoading || $[46] !== detailLink || $[47] !== showDetail) {
    t16 = detailLink && <Button loading={clickLoading} className="w-auto" color="primary" onClick={showDetail} text={"\u0645\u0634\u0627\u0647\u062F\u0647 \u0627\u0642\u0627\u0645\u062A\u06AF\u0627\u0647 \u0648 \u0631\u0632\u0631\u0648"} />;
    $[45] = clickLoading;
    $[46] = detailLink;
    $[47] = showDetail;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t15 || $[50] !== t16) {
    t17 = <div className="mt-auto flex flex-row justify-end gap-4 pt-4">{t15}{t16}</div>;
    $[49] = t15;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== closeGallery || $[53] !== isGalleryModalOpen || $[54] !== t10 || $[55] !== t12 || $[56] !== t14 || $[57] !== t17) {
    t18 = <Modal className="!h-[90vh] !max-h-full overflow-y-scroll rounded-xl md:!h-full md:rounded-none lg:!h-[640px] lg:!w-[720px]" headerStyle="!h-16 pb-2" onClose={closeGallery} open={isGalleryModalOpen} showCloseButton={true} title={t10} titleClassName="line-clamp-2" usePortal={true}>{t12}{t14}{t17}</Modal>;
    $[52] = closeGallery;
    $[53] = isGalleryModalOpen;
    $[54] = t10;
    $[55] = t12;
    $[56] = t14;
    $[57] = t17;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== t18 || $[60] !== t8) {
    t19 = <>{t8}{t18}</>;
    $[59] = t18;
    $[60] = t8;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  return t19;
};